import Layout from "Layout";
import { Tabs, Modal, ConfigProvider } from 'antd';
import type { TabsProps } from 'antd';
import { Router, useNavigate, useParams, useRoutes } from "react-router-dom";
import DetailProductInfo from "./component/detailProductInfo";
import DetailStats from "./component/detailStats";
import DetailProductFeature from "./component/detailFeatureTab";
import DetailGuildTab from "./component/detailGuildTab";
import { platformDetail, StoreProductDetailtype } from "./type";
import ProductDetailPlatformInfo from "./component/detailPlatformDetail";
import { useEffect, useState } from "react";
import BuyProductPopUp from "./component/buyProductPopUp";
import { useTranslation } from "react-i18next";
import { REACT_APP_PAYPAL_MODE } from 'env';
import { API_URL } from "env";
import { toast, ToastContainer } from "react-toastify";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import { mixPostV1 } from "services/mixin";

// @ts-ignore: Allow importing SCSS modules without type declarations
import 'pages/Store/store.scss';
import { Helmet } from "react-helmet-async";



const platformDetailData: { en: platformDetail[], vi: platformDetail[] } = {
    vi: [
        {
            name: "All Platforms",
            code: "all",
            icon: "🌐",
            description: "Tương thích với tất cả các nền tảng giao dịch phổ biến như MetaTrader 4, MetaTrader 5, cTrader.",
            features: [
                "Tương thích với nhiều nền tảng giao dịch",
                "Dễ dàng chuyển đổi giữa các nền tảng",
                "Hỗ trợ đa dạng các loại tài sản",
            ],
            downloadLink: "#",
        },
        {
            name: "MetaTrader 4",
            code: "MT4",
            icon: "🚀",
            description: "Nền tảng giao dịch phổ biến với giao diện thân thiện và nhiều công cụ phân tích kỹ thuật.",
            features: [
                "Hỗ trợ Expert Advisors (EAs)",
                "Giao dịch tự động và thủ công",
                "Công cụ phân tích kỹ thuật đa dạng",
            ],
            downloadLink: "https://www.metatrader4.com/en/download",
        },
        {
            name: "MetaTrader 5",
            code: "MT5",
            icon: "🚀",
            description: "Phiên bản nâng cấp của MT4 với nhiều tính năng mới và hỗ trợ thêm nhiều loại tài sản.",
            features: [
                "Hỗ trợ nhiều loại tài sản hơn (cổ phiếu, hàng hóa)",
                "Lịch kinh tế tích hợp",
                "Công cụ phân tích nâng cao",
            ],
            downloadLink: "https://www.metatrader5.com/en/download",
        },
        {
            name: "cTrader",
            code: "cTrader",
            icon: "🚀",
            description: "Nền tảng giao dịch hiện đại với giao diện trực quan và các công cụ giao dịch tiên tiến.",
            features: [
                "Tương thích với Windows & Mac",
                "Hỗ trợ VPS và Local PC",
            ],
            downloadLink: "https://www.spotware.com/downloads/ctrader",
        }
    ],
    en: [
        {
            name: "All Platforms",
            code: "all",
            icon: "🌐",
            description: "Compatible with all popular trading platforms such as MetaTrader 4, MetaTrader 5, cTrader.",
            features: [
                "Compatible with multiple trading platforms",
                "Easy switching between platforms",
                "Supports a wide range of asset types",
            ],
            downloadLink: "#",
        },
        {
            name: "MetaTrader 4",
            code: "MT4",
            icon: "🚀",
            description: "A popular trading platform with a user-friendly interface and a wide range of technical analysis tools.",
            features: [
                "Supports Expert Advisors (EAs)",
                "Automated and manual trading",
                "Diverse technical analysis tools",
            ],
            downloadLink: "https://www.metatrader4.com/en/download",
        },
        {
            name: "MetaTrader 5",
            code: "MT5",
            icon: "🚀",
            description: "An upgraded version of MT4 with new features and support for more asset types.",
            features: [
                "Supports more asset types (stocks, commodities)",
                "Integrated economic calendar",
                "Advanced analysis tools",
            ],
            downloadLink: "https://www.metatrader5.com/en/download",
        },
        {
            name: "cTrader",
            code: "cTrader",
            icon: "🚀",
            description: "A modern trading platform with an intuitive interface and advanced trading tools.",
            features: [
                "Compatible with Windows & Mac",
                "Supports VPS and Local PC",
            ],
            downloadLink: "https://www.spotware.com/downloads/ctrader",
        }
    ]
}

const PAYPAL_CLIENT_ID = REACT_APP_PAYPAL_MODE === 'live'
    ? 'AZFX-UaOLaMkQqwVbbt7w0cInOZ5uANZ6UMrh4OY0mL0eA3m_ZSpXt1ubhu6F_Jn9EDbVgKqUIom8ZYH'  // Live
    : 'Abn2V5kg80p1v6NyUR2OnP4cpJ23R1b9sjPmNHKFzQvkcV4yos94dr19y8lJHEsAisLSrXlQzSxQGdxL'; // Sandbox

const initialOptions = {
    "clientId": PAYPAL_CLIENT_ID,
    "currency": "USD",
    "intent": "capture",
    "components": "buttons",
    "enable-funding": "venmo,paylater",
    "disable-funding": ""
};

const StoreProductDetail = () => {
    const { id } = useParams<{ id: string }>();
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
    const [isBuyOpen, setIsBuyOpen] = useState(false);
    const platformDetail = language === 'vi' ? platformDetailData.vi : platformDetailData.en;
    const [product, setProduct] = useState<any | null>(null);
    const [userId, setUserId] = useState<number>()
    const [orderId, setOrderId] = useState<string>('')
    const [paymentId, setPaymentId] = useState<number>()
    const [isProductIsPurchased, setIsProductIsPurchased] = useState<boolean>(false);
    const [purchasedId, setPurchasedId] = useState<number | null>(null);

    async function checkProductIsPurchased(userId: number, productId: string) {
        try {
            const response = await fetch(`${API_URL}/api/v1/be/orders?user_id=${userId}&tool_id=${productId}&status=paid`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                },
            });
            const resData = await response.json();
            if (resData.status) {
                setPurchasedId(resData.data.items.length > 0 ? resData.data.items[0].id : null);
                return resData.data.total > 0;
            } else {
                return false;
            }
        } catch (error) {
            console.error("Error checking product purchase status:", error);
            return false;
        }
    }

    async function fetchProductById(productId: string) {
        try {
            setStatus('loading');
            const response = await fetch(`${API_URL}/api/v1/be/tools/${productId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
                },
            });
            const resData = await response.json();
            if (resData.status) {
                const data = resData.data;
                const lang = i18n.language;
                const mappedProduct: any = {
                    id: data.id,
                    image: data.image_url || "",
                    platform: data.platform,
                    indicator: data.featured ? "New" : "",
                    name: data.title && data.title[lang] ? data.title[lang] : 'No Title',
                    star: data.star || 0,
                    review: data.review || 0,
                    description: data.subtitle && data.subtitle[lang] ? data.subtitle[lang] : 'No Description',
                    price: data.price ? `$${data.price}` : '',
                    discount: data.price_discount ? `$${data.price_discount}` : undefined,
                    finalPrice: data.price_finalize ? `$${data.price_finalize}` : '',
                    fileBot: data.file_bot_url || '',
                    numBuyer: 500,
                    features: {
                        mainFeature: data.main_feature && Array.isArray(data.main_feature[lang]) ? data.main_feature[lang] : (typeof data.main_feature[lang] === 'string' ? [data.main_feature[lang]] : []),
                        detail: data.description && data.description[lang] ? data.description[lang] : '',
                    },
                    guide: {
                        video: {
                            duration: data.video_duration || '',
                            url: data.video_url && data.video_url[lang] ? data.video_url[lang] : '',
                            difficulty: '', // Not provided in API
                            language: [
                                { name: "English", icon: "🇬🇧", url: data.video_url && data.video_url['en'] ? data.video_url['en'] : '' },
                                { name: "Vietnamese", icon: "🇻🇳", url: data.video_url && data.video_url['vi'] ? data.video_url['vi'] : '' }
                            ]
                        },
                        document: {
                            totalPages: data.pdf_page || 0,
                            url: data.file_pdf_url || '',
                            option: ["PDF"],
                            language: [
                                { name: "English", icon: "🇬🇧", url: data.file_pdf_url?.en || '' },
                                { name: "Vietnamese", icon: "🇻🇳", url: data.file_pdf_url?.vi || '' }
                            ]
                        }
                    }
                }
                setProduct(mappedProduct);
                setStatus('success');
            }
        }
        catch (error) {
            console.error("Error fetching product:", error);
            setStatus('error');
        }
    }

    // Fetch product when id changes
    useEffect(() => {
        if (id) {
            fetchProductById(id);
            if (userId) {
                checkProductIsPurchased(userId, id).then((isPurchased) => {
                    setIsProductIsPurchased(isPurchased);
                });
            }
        }
        
    }, [id, i18n.language, userId]);

    useEffect(() => {
        const localeData = localStorage.getItem('user')
        if (localeData) {
            const idData = JSON.parse(localeData).id
            setUserId(idData)
        }
    }, [])

    // Tabs items
    const items: TabsProps['items'] = [
        {
            key: '1',
            style: { width: '100%' },
            label: (
                <div className="flex items-center justify-center gap-2 py-1 px-16 btn-product-detail-text">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-zap h-5 w-5"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
                    {t('store_detail_tab_1')}
                </div>
            ),
            children: <DetailProductFeature
                mainFeature={product ? product.features.mainFeature : []}
                detail={product ? product.features.detail : ''}
            />,
        },
        {
            key: '2',
            style: { width: '100%' },
            label: (
                <div className="flex items-center justify-center gap-2 py-1 px-16 btn-product-detail-text">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-file-text h-5 w-5"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path><path d="M14 2v4a2 2 0 0 0 2 2h4"></path><path d="M10 9H8"></path><path d="M16 13H8"></path><path d="M16 17H8"></path></svg>
                    {t('store_detail_tab_2')}
                </div>
            ),
            children: <DetailGuildTab
                video={product && product.guide ? product.guide.video : null}
                document={product && product.guide ? product.guide.document : null}
            />,
        },
    ];
    return (
        <Layout>
            <Helmet>
                <title>{product ? product.name : 'Store Product Detail'}</title>
                <meta name="description" content={product ? product.description : 'Store Product Detail'} />
            </Helmet>
            <ConfigProvider
                theme={{
                    token: {
                        colorPrimary: '#2563EB',
                        borderRadius: 3,
                    },
                    components: {
                        Tabs: {
                            itemActiveColor: '#E9B460',
                            inkBarColor: '#E9B460',
                            colorText: '#374151',
                            itemSelectedColor: '#E9B460',
                            itemHoverColor: '#E9B460',
                            margin: 0,
                        }
                    }
                }}
            >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left column  */}
                    <div className="lg:col-span-2 space-y-8 ">
                        {
                            product && status === 'success' &&
                            <>
                                <DetailProductInfo
                                    image={product ? product.image : ''}
                                    name={product ? product.name : ''}
                                    star={product ? product.star : 0}
                                    review={product ? product.review : 0}
                                    numBuyer={0}
                                    description={product ? product.description : ''}
                                />
                                <Tabs
                                    defaultActiveKey="1"
                                    centered
                                    items={items}
                                    className="store-container"
                                    style={{
                                        background: 'white', borderRadius: '3px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    }} />
                            </>
                        }
                        {
                            status === 'loading' &&
                            <div>Loading...</div>
                        }
                        {
                            status === 'error' &&
                            <div>Error loading product. Please try again later.</div>
                        }
                    </div>
                    {/* Right column  */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24 space-y-6">
                            <div className="rounded-[3px] shadow-lg p-6 store-container">
                                <div className="text-center mb-6">
                                    {/* <div className="flex items-center justify-center gap-3 mb-2">
                                        <span className="text-3xl font-bold text-gray-900">
                                            {product.finalPrice} USD
                                        </span>
                                        {
                                            product.discount && (
                                                <span className="text-sm text-gray-500 line-through">
                                                    {product.price}
                                                </span>
                                            )
                                        }
                                    </div> */}
                                    {/* <div className="text-sm text-gray-600">{t('store_detail_buy_desc')}</div>
                                    <div className="text-sm text-green-600 font-semibold">{t('store_detail_buy_save')} {product.discount} USD</div> */}
                                </div>


                                {/* <button
                                    onClick={() => setIsBuyOpen(true)}
                                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 !rounded-lg hover:bg-blue-700 transition duration-300 font-semibold text-lg mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-credit-card h-5 w-5"><rect width="20" height="14" x="2" y="5" rx="2"></rect><line x1="2" x2="22" y1="10" y2="10"></line></svg>
                                    {t('store_detail_buy_buyNowWithPaypal')}
                                </button> */}

                                {(isProductIsPurchased || product?.finalPrice === "") ? (
                                    <div className="text-center text-green-600 font-semibold flex flex-col gap-4">
                                        {product?.finalPrice === "" ? t('store_free'):t('product_tool_alreadyPurchased')}
                                        <div className="flex flex-col">
                                           {
                                                product && purchasedId && (
                                                    <button 
                                                        onClick={() => {
                                                            window.location.href = `/profile/inventory/${purchasedId}`;
                                                        }}
                                                        className="w-full flex items-center justify-center gap-2 bg-gray-200 text-gray-900 px-6 py-3 !rounded-lg hover:bg-gray-300 transition duration-300 font-semibold text-lg my-4">
                                                            {t('product_tool_buy_reciept')}
                                                    </button>
                                                )
                                           }
                                            <button
                                                onClick={() => {
                                                    if (product && product.fileBot) {
                                                        window.open(product.fileBot, '_blank');
                                                    } else {
                                                        console.log('No file available for download');
                                                    }
                                                }}
                                            className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 !rounded-lg hover:bg-blue-700 transition duration-300 font-semibold text-lg">
                                                {t('product_tool_download')}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <PayPalScriptProvider options={initialOptions}>
                                            <PayPalButtons
                                                style={{
                                                    shape: "rect",
                                                    layout: "vertical",
                                                    color: "gold",
                                                    label: "paypal",
                                                }}
                                                createOrder={async () => {
                                                    try {
                                                        const orderData = await mixPostV1('/api/v1/be/order-tools/paypal/create', {
                                                            user_id: userId,
                                                            tool_id: Number(id),
                                                            return_url: `${window.location.origin}/stores`,
                                                            cancel_url: `${window.location.origin}/stores/${id}`,
                                                            quantity: 1
                                                        });

                                                        // const orderData = await mixPostV1('/api/v1/be/orders/paypal/create', {
                                                        //     user_id: userId,
                                                        //     product_id: 205,
                                                        //     return_url: `${window.location.origin}/stores`,
                                                        //     cancel_url: `${window.location.origin}/stores/${id}`,
                                                        //     quantity: 1
                                                        // });
                                                        if (orderData?.status) {
                                                            setOrderId(orderData.data.order_id);
                                                            setPaymentId(orderData.data.payment_id);
                                                            return orderData.data.order_id;
                                                        } else {
                                                            if (!orderData.status && orderData.error.already_purchased) {
                                                                toast.error(orderData.error.already_purchased, {
                                                                    position: "top-right",
                                                                    autoClose: 3000,
                                                                    hideProgressBar: false,
                                                                    closeOnClick: false,
                                                                    pauseOnHover: true,
                                                                    draggable: true,
                                                                    progress: undefined,
                                                                    theme: "light",
                                                                });
                                                            }
                                                            // const errorDetail = orderData?.details?.[0];
                                                            // const errorMessage = errorDetail
                                                            //     ? `${errorDetail.issue} ${errorDetail.description} (${orderData.debug_id})`
                                                            //     : JSON.stringify(orderData);
                                                            // throw new Error(errorMessage);
                                                        }
                                                    } catch (error: any) {
                                                        toast.error('There is an error, please try again', {
                                                            position: "top-right",
                                                            autoClose: 3000,
                                                            hideProgressBar: false,
                                                            closeOnClick: false,
                                                            pauseOnHover: true,
                                                            draggable: true,
                                                            progress: undefined,
                                                            theme: "light",
                                                        });
                                                        throw error;
                                                    }
                                                }}
                                                onApprove={async (data, actions) => {
                                                    try {
                                                        // Sử dụng data.orderID trực tiếp, không dùng state
                                                        const orderData = await mixPostV1('/api/v1/be/order-tools/paypal/capture', {
                                                            paypal_order_id: orderId,
                                                            payment_id: paymentId,
                                                        });

                                                        // const orderData = await mixPostV1('/api/v1/be/orders/paypal/capture', {
                                                        //     paypal_order_id: orderId,
                                                        //     payment_id: paymentId,
                                                        // });

                                                        if (!orderData || typeof orderData !== 'object') {
                                                            throw new Error('Invalid response from server');
                                                        }

                                                        const errorDetail = orderData?.data;
                                                        if (orderData && orderData.status) {
                                                            toast.success('Payment successfully', {
                                                                position: "top-right",
                                                                autoClose: 3000,
                                                                hideProgressBar: false,
                                                                closeOnClick: false,
                                                                pauseOnHover: true,
                                                                draggable: true,
                                                                progress: undefined,
                                                                theme: "light",
                                                            });
                                                        }

                                                        console.log(errorDetail);

                                                        if (errorDetail?.issue === "INSTRUMENT_DECLINED") {
                                                            return actions.restart();
                                                        } else if (errorDetail) {
                                                            // throw new Error(`${errorDetail.description} (${orderData.debug_id})`);
                                                            return
                                                        } else if (orderData && orderData.data) {
                                                            const transaction =
                                                                orderData.data;

                                                            toast.error(`Transaction ${transaction.status}: ${transaction.capture_id}.`, {
                                                                position: "top-right",
                                                                autoClose: 3000,
                                                                hideProgressBar: false,
                                                                closeOnClick: false,
                                                                pauseOnHover: true,
                                                                draggable: true,
                                                                progress: undefined,
                                                                theme: "light",
                                                            });

                                                            console.log("Capture result", orderData);
                                                        }
                                                    } catch (error) {
                                                        console.error(error);
                                                        toast.error(`Sorry, your transaction could not be processed...${error}`, {
                                                            position: "top-right",
                                                            autoClose: 3000,
                                                            hideProgressBar: false,
                                                            closeOnClick: false,
                                                            pauseOnHover: true,
                                                            draggable: true,
                                                            progress: undefined,
                                                            theme: "light",
                                                        });
                                                    }
                                                }}
                                            />
                                        </PayPalScriptProvider>
                                        <div className="text-xs text-gray-500 text-center mb-4">🔒 {t('store_detail_buy_section_title')}</div>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-center gap-2 text-green-600">
                                                <span>✓</span><span>{t('store_detail_buy_section_desc2')}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-green-600">
                                                <span>✓</span><span>{t('store_detail_buy_section_desc3')}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-green-600">
                                                <span>✓</span><span>{t('store_detail_buy_section_desc4')}</span>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <Modal
                                    title={
                                        <div className="flex flex-col px-6 pt-4 rounded items-start bg-gradient-to-r from-blue-50 to-indigo-50">
                                            <h2 className="!text-2xl font-bold text-gray-900 !mb-0">{t('store_checkoutTitle')}</h2>
                                            <p className="text-gray-600">{t('store_checkoutDesc')}</p>
                                        </div>
                                    }
                                    styles={{
                                        header: {
                                            padding: 0,
                                        },
                                        content: {
                                            padding: 0,
                                        },
                                        body: {
                                            paddingLeft: 8,
                                            paddingRight: 8,
                                            paddingBottom: 2,
                                            paddingTop: 2,
                                        }
                                    }}
                                    style={{
                                        top: 20,
                                    }}
                                    open={isBuyOpen}
                                    onCancel={() => setIsBuyOpen(false)}
                                    footer={null}
                                    width={600}
                                >
                                    {product && <BuyProductPopUp {...product} id={product.id ?? 0} />}
                                </Modal>


                            </div>
                            {
                                product &&
                                <ProductDetailPlatformInfo
                                    code={platformDetail.find(p => p.code === product.platform)?.code || ''}
                                    name={platformDetail.find(p => p.code === product.platform)?.name || ''}
                                    icon={platformDetail.find(p => p.code === product.platform)?.icon || ''}
                                    description={platformDetail.find(p => p.code === product.platform)?.description || ''}
                                    features={platformDetail.find(p => p.code === product.platform)?.features || []}
                                    downloadLink={platformDetail.find(p => p.code === product.platform)?.downloadLink || ''}
                                />
                            }
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-[3px] p-6 ">
                                <h3 className="font-semibold !text-gray-900 mb-2 !text-xl">{t('store_detail_support_title')}</h3>
                                <p className="text-sm !text-gray-600 mb-4">{t('store_detail_support_desc')}</p>
                                <div className="space-y-2 text-sm">
                                    <div className="!text-black hidden">📧 beqlumir@gmail.com</div>
                                    <div className="!text-black">📱 +84 941 753 139</div>
                                    <div className="!text-black">💬 {t('store_detail_support_web')}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <ToastContainer />
            </ConfigProvider>
        </Layout>
    );
}

export default StoreProductDetail;